import { CAPTURE_DASHBOARD_PERMISSION } from '@/core/guards/capture-dashboard.guard';
import { NotificationTypeEnum } from '@/core/services/profile/me-profile.model';
import { PermissionsService } from '@/core/services/permissions/permissions.service';
import {
  canSeeUsageMetrics,
  canSeeUsageMetricsItem,
  USAGE_METRICS_ROUTE,
} from '@/core/usage-metrics/usage-metrics-access';
import { LayoutService } from '@/layout/service/layout.service';
import { MenuStateService } from '@/layout/service/menu-state.service';

import {
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  VERSION,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { version as appVersion } from '../../../../package.json';
import { AppConfiguratorService } from './app.configurator.service';

@Component({
  selector: 'app-configurator',
  standalone: true,
  imports: [
    FormsModule,
    SelectButtonModule,
    DrawerModule,
    ToggleSwitchModule,
    RouterLink,
    TranslatePipe,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './app.configurator.component.html',
})
export class AppConfiguratorComponent implements OnInit {
  private readonly permissions = inject(PermissionsService);
  private readonly _configuratorService = inject(AppConfiguratorService);
  private readonly layoutService = inject(LayoutService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly menuStateService = inject(MenuStateService);
  private readonly messageService = inject(MessageService);

  protected canSeeCaptureDashboard = computed(
    () =>
      this.permissions.isMasterWhitelabel() &&
      (this.permissions.isMasterAdmin() ||
        this.permissions.hasPermission(CAPTURE_DASHBOARD_PERMISSION)),
  );

  protected canSeeCameraGroupTab = computed(
    () =>
      this.permissions.hasPermission('can_see_camera_group_tab') ||
      this.permissions.isMasterAdmin(),
  );

  protected canSeeUserTab = computed(
    () =>
      this.permissions.hasPermission('can_see_user_tab') ||
      this.permissions.isMasterAdmin(),
  );

  protected canSeePermissionLevelTab = computed(
    () =>
      this.permissions.hasPermission('can_see_permission_levels_tab') ||
      this.permissions.isMasterAdmin(),
  );

  protected canSeeServicePackagesTab = computed(
    () =>
      (this.permissions.hasPermission('can_see_service_packages_tab') ||
        this.permissions.isMasterAdmin()) &&
      this.permissions.isMasterWhitelabel(),
  );

  protected canSeeWhatsappIntegrationTab = computed(
    () =>
      (this.permissions.hasPermission('can_see_whatsapp_integration_menu') ||
        this.permissions.isMasterAdmin()) &&
      this.permissions.hasWhatsappIntegration(),
  );

  protected whatsappIntegrationRoute = computed(() => {
    if (this.permissions.isMasterWhitelabel()) {
      return ['/configuracoes/gerenciamento/integracao-whatsapp'];
    }

    const whitelabel = this.permissions.getMe()?.whitelabel;
    return ['/configuracoes/gerenciamento/integracao-whatsapp', whitelabel?.id];
  });

  protected whatsappIntegrationQueryParams = computed(() => {
    if (this.permissions.isMasterWhitelabel()) return null;

    return { name: this.permissions.getMe()?.whitelabel?.name };
  });

  protected canSeeFaceLowQuality = computed(() => {
    const me = this.permissions.getMe();
    return (
      !!me?.whitelabel?.services?.has_face_recognition &&
      !!me?.cameraGroupPermissions?.canSeeFaceComparisonEvents &&
      (this.permissions.hasPermission('can_see_facial_menu') ||
        this.permissions.isMasterAdmin())
    );
  });

  protected canSeeUsageMetricsItem = computed(() =>
    canSeeUsageMetricsItem(this.permissions.getMe()),
  );

  protected canSeeUsageMetrics = computed(() =>
    canSeeUsageMetrics(this.permissions.getMe()),
  );

  protected readonly usageMetricsRoute = USAGE_METRICS_ROUTE;

  protected searchableItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    if (this.canSeeCameraGroupTab()) {
      items.push({
        label: this.translateService.instant('configurator.camera_groups'),
        icon: 'pi pi-objects-column',
        routerLink: ['/group-cameras'],
      });
    }
    if (this.canSeeUserTab()) {
      items.push({
        label: this.translateService.instant('configurator.users'),
        icon: 'pi pi-users',
        routerLink: ['/users'],
      });
    }
    if (this.canSeePermissionLevelTab()) {
      items.push({
        label: this.translateService.instant('configurator.permission_levels'),
        icon: 'pi pi-shield',
        routerLink: ['/permission-levels'],
      });
    }
    if (this.canSeeFaceLowQuality()) {
      items.push({
        label: 'Faces Desqualificadas',
        icon: 'pi pi-image',
        routerLink: ['/face-low-quality'],
      });
    }
    if (this.canSeeCaptureDashboard()) {
      items.push({
        label: 'Dashboard de Capturas LPR e Facial',
        icon: 'pi pi-chart-bar',
        routerLink: ['/configuracoes/dashboard-capturas'],
      });
    }
    if (this.canSeeWhatsappIntegrationTab()) {
      items.push({
        label: this.translateService.instant(
          'configurator.whatsapp_integration',
        ),
        icon: 'pi pi-whatsapp',
        command: () =>
          this.router.navigate(this.whatsappIntegrationRoute(), {
            queryParams: this.whatsappIntegrationQueryParams(),
          }),
      });
    }
    if (this.canSeeServicePackagesTab()) {
      items.push({
        label: this.translateService.instant('configurator.service_packages'),
        icon: 'pi pi-box',
        routerLink: ['/configuracoes/gerenciamento/pacotes-servico'],
      });
    }
    if (this.canSeeUsageMetricsItem()) {
      items.push({
        label: this.translateService.instant('configurator.usage_metrics'),
        icon: 'pi pi-chart-bar',
        command: () => this.openUsageMetrics(),
      });
    }

    return items;
  });

  constructor() {
    this.currentLanguage = this.translateService.getDefaultLang();
    effect(() => {
      this.menuStateService.setConfiguratorItems(this.searchableItems());
    });
  }
  @Input({ transform: booleanAttribute }) simple = false;
  @Input() location = 'app';

  currentLanguage = this._configuratorService.getCurrentLanguage();
  languages = this._configuratorService.getAvailableLanguages();

  themeOptions = [
    { name: 'Light', value: false },
    { name: 'Dark', value: true },
  ];

  menuThemeOptions = computed(() =>
    this._configuratorService.getMenuThemeOptions(this.darkTheme()),
  );

  notificationPreference = this._configuratorService.notificationPreference;
  primaryColors = computed(() => this._configuratorService.getPrimaryColors());

  selectedPrimaryColor = computed(
    () => this.layoutService.layoutConfig().primary,
  );
  selectedPreset = computed(() => this.layoutService.layoutConfig().preset);
  darkTheme = computed(() => this.layoutService.layoutConfig().darkTheme);
  visible = computed(
    () => this.layoutService.layoutState().configSidebarVisible,
  );

  notificationMap = Object.entries(NotificationTypeEnum)
    .filter(([key]) => !['pushChat', 'pushNeural'].includes(key))
    .map(([key, label]) => ({ key, label, visible: true }));

  async ngOnInit() {
    await this._configuratorService.checkNotificationStatus();
    this.checkIfBlocked();
  }

  readonly angularVersion = VERSION.full;
  readonly appVersion = appVersion;

  isNotificationBlocked = signal(false);

  checkIfBlocked() {
    const isBlocked = Notification.permission === 'denied';
    const pushAllActive = this.notificationPreference().pushAll;
    this.isNotificationBlocked.set(isBlocked && pushAllActive);
  }
  onDrawerHide() {
    this.layoutService.hideConfigSidebar();
  }

  protected openUsageMetrics(): void {
    if (!this.canSeeUsageMetrics()) {
      this.showUsageMetricsAccessDenied();
      return;
    }

    this.router.navigate([this.usageMetricsRoute]);
  }

  protected showUsageMetricsAccessDenied(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant(
        'usage_metrics.access.denied_summary',
      ),
      detail: this.translateService.instant(
        'usage_metrics.access.denied_detail',
      ),
    });
  }

  updateLanguage() {
    this._configuratorService.updateLanguage(this.currentLanguage);
  }

  updateColors(event: any, type: string, color: any) {
    this._configuratorService.updateColors(type, color);
    event.stopPropagation();
  }

  onPresetChange(event: any) {
    this._configuratorService.changePreset(event);
  }

  toggleDarkMode() {
    this._configuratorService.toggleDarkMode();
  }

  async updateNotificationPreference(key: string, value: boolean) {
    const result = await this._configuratorService.updateNotificationPreference(
      key,
      value,
    );
    this.checkIfBlocked();

    return result;
  }

  getNotificationValue(key: string): boolean {
    return this._configuratorService.getNotificationValue(key);
  }
}
